<template>
  <div class="change-status__container">
    <change-status-header :displayName="data.displayName" />
    <div class="change-status__content">
      <div v-if="changeStatusAtFundLevel" class="shareclasses-data__fund">
        <change-status-funds-group
          :statusArray="fundStatusArray"
          :statusText="fundStatusText"
          :options="fundOptions"
          :newStatus="newStatus"
          :newStatusIcon="newStatusIcon"
          :newStatusText="newStatusText"
          :isPending="data.pending"
          @onSelect="selectNewStatusFund"
        />
      </div>
      <loader-spinner v-if="loading" />
      <div
        v-else
        class="shareclasses-data__container"
        :style="{ height: `calc(100% - ${fundDataTableHeight}px)` }"
      >
        <change-status-table
          :columns="['Ticker', 'Current Status', 'Change Status']"
          :expanded="true"
        >
          <template slot="title">
            <span>Fund Name</span>
          </template>
          <template v-for="(shareClass, index) in shareClasses">
            <change-status-shareclass
              :class="{ 'group-begin': true, 'group-end': selectedClient }"
              :key="`shareclass-${index}`"
              :fundId="shareClass.id"
              :ticker="shareClass.ticker"
              :statusArray="shareClass.statusArray"
              :areFundSeries="areFundSeries"
              :statusText="shareClass.status"
              :options="shareClass.options"
              :newStatus="shareClass.newStatus"
              :newStatusIcon="shareClass.newStatusIcon"
              :newStatusText="shareClass.newStatusText"
              :haveClients="true"
              :isPending="shareClass.isPending"
              @onSelect="selectNewStatusShareclass($event, index)"
            >
              {{ getShareclassDisplayName(shareClass) }}
            </change-status-shareclass>
            <change-status-clients-header
              v-if="allClientsView"
              :key="`client-${shareClass.id}`"
              :display="shareClass.displayClients"
              @toggleClient="toggleClient($event, index)"
            />
            <template v-if="allClientsView">
              <change-status-clients-list
                class="mds-data-table__row client-row"
                v-show="shareClass.displayClients"
                v-for="client in shareClass.clients"
                :key="`client-details-${shareClass.id}-${client.id}`"
                :fundId="shareClass.id"
                :category="client.category"
                :status="client.status"
                :newStatus="client.newStatus"
                :newStatusIcon="client.newStatusIcon"
                :newStatusText="client.newStatusText"
                :isPending="client.isPending"
              >
                {{ client.name }}
              </change-status-clients-list>
            </template>
          </template>
        </change-status-table>
      </div>
    </div>
    <div class="change-status__actions">
      <mds-button variation="primary" class="change-status__save-changes"
        @click="saveChanges"
        :disabled="processing || disableSubmit"
      >
        Change Status
      </mds-button>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'vuex';
import {
  cloneDeep,
  find,
  get,
  orderBy,
  merge,
  sortBy,
} from 'lodash';

import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import LoaderSpinner from '@/components/atoms/LoadingSpinner';
import ChangeStatusHeader from '@/components/atoms/ChangeStatusHeader';
import ChangeStatusTable from '@/components/atoms/ChangeStatusTable';
import ChangeStatusClientsHeader from '@/components/atoms/ChangeStatusClientsHeader';
import ChangeStatusClientsList from '@/components/molecules/ChangeStatusClientsList';
import ChangeStatusFundsGroup from '@/components/molecules/ChangeStatusFundsGroup';
import ChangeStatusShareclass from '@/components/molecules/ChangeStatusShareclass';
import {
  objectCount,
  mapToNameCount,
  extractNameByMayorCount,
  getSchemaIndexFromStatus,
  applySchemaToOptions,
  reviewErrorFromResponse,
} from '@/utils';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import * as ShareClassesService from '@/services/ShareClasses';
/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import * as PendingFundsService from '@/services/PendingFunds';

/**
 * Modal Content
 * @Component('change-status')
 */
export default {
  name: 'change-status',
  components: {
    LoaderSpinner,
    ChangeStatusHeader,
    ChangeStatusFundsGroup,
    ChangeStatusTable,
    ChangeStatusShareclass,
    ChangeStatusClientsHeader,
    ChangeStatusClientsList,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'Modal:Organisms/ChangeStatus' }),
  ],
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      processing: false,
      applyGlobalStatus: true,
      selectedFund: {},
      newStatus: 0,
      newStatusText: '',
      newStatusIcon: '',
      rowData: [],
      shareClasses: [],
      disableSubmit: true,
      emptyOption: {
        label: 'Select',
        icon: '',
        config: {
          show: true,
          disabled: false,
        },
        value: 0,
      },
      defaultOptions: [
        {
          label: 'Select',
          icon: '',
          config: {
            show: true,
            disabled: false,
          },
          value: 0,
        },
        {
          label: 'Off Watch',
          icon: 'Approved',
          config: {
            show: true,
            disabled: false,
          },
          value: 1,
        },
        {
          label: 'On Watch',
          icon: 'OnWatch',
          config: {
            show: true,
            disabled: false,
          },
          value: 3,
        },
        {
          label: 'Removed',
          icon: 'Removed',
          config: {
            show: true,
            disabled: false,
          },
          value: 2,
        },
      ],
      schema: [
        {
          status: ['*'], // Multiple statuses
          options: ['Select', 'Off Watch', 'On Watch', 'Removed'],
        },
        {
          status: ['Approved'],
          options: [
            'Select',
            {
              label: 'Off Watch',
              config: { disabled: true },
            },
            'On Watch',
            'Removed',
          ],
        },
        {
          status: ['OnWatch'],
          options: [
            'Select',
            'Off Watch',
            {
              label: 'On Watch',
              config: { disabled: true },
            },
            'Removed',
          ],
        },
        {
          status: ['Removed'],
          options: [
            'Select',
            {
              label: 'Off Watch',
              config: { disabled: true },
            },
            {
              label: 'On Watch',
              config: { disabled: true },
            },
            {
              label: 'Removed',
              config: { disabled: true },
            },
          ],
        },
        {
          status: ['Approved', 'OnWatch'],
          options: ['Select', 'Off Watch', 'On Watch', 'Removed'],
        },
        {
          status: ['Approved', 'Removed'],
          options: [
            'Select',
            {
              label: 'Off Watch',
              config: { disabled: true },
            },
            'On Watch',
            'Removed',
          ],
        },
        {
          status: ['OnWatch', 'Removed'],
          options: [
            'Select',
            'Off Watch',
            {
              label: 'On Watch',
              config: { disabled: true },
            },
            'Removed',
          ],
        },
      ],
      request: null,
      requestController: null,
      requestUrl: null,
    };
  },
  computed: {
    ...mapState({
      fiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.grid.client.selected,
      clientName: (state) => state.grid.client.name,
    }),
    areFundSeries() {
      return this.data.__isTargetSeries && !this.allClientsView;
    },
    fundApprovalStatus() {
      return this.data.approvalStatus;
    },
    fundStatusArray() {
      const fundStatusArrayWithCount = objectCount(this.fundApprovalStatus, 'name');
      const fundStatusArray = sortBy(mapToNameCount(fundStatusArrayWithCount), 'name');
      return fundStatusArray;
    },
    fundStatusText() {
      const statusText = this._status(this.fundStatusArray, 'name');
      return statusText;
    },
    fundOptions() {
      const statuses = this.fundStatusArray.map((item) => item.name);
      const index = getSchemaIndexFromStatus(this.schema, statuses);
      const options = applySchemaToOptions(this.schema[index].options, this.defaultOptions);
      return options;
    },
    allClientsView() {
      const allClientsView = !this.selectedClient;
      return allClientsView;
    },
    changeStatusAtFundLevel() {
      const enoughShareClasses = this.shareClasses.length > 0;
      return this.allClientsView || enoughShareClasses;
    },
    fundDataTableHeight() {
      /*
       * The shareclass-data table container needs a specific height
       * for the property: overflow-y: scroll
       * If the change status at fund level is present add the
       * height of the table
       */
      let fundDataTableHeight;
      if (this.changeStatusAtFundLevel) {
        fundDataTableHeight = 164;
      } else {
        fundDataTableHeight = 62;
      }
      return fundDataTableHeight;
    },
    isLogoutTheCurrentRoute() {
      return this.$route.name === 'logout';
    },
  },
  created() {
    this.selectedFund = cloneDeep(this.data);
    const serviceMethod = this.allClientsView ? ShareClassesService.getShareClassesByFiduciaryAndFundId
      : ShareClassesService.getShareClassesByClientAndFundId;

    if (this.allClientsView || !this.data.__isTargetSeries) {
      // ShareClassesService.getShareClassesByFiduciaryAndFundId || ShareClassesService.getShareClassesByClientAndFundId
      serviceMethod({
        fiduciary: this.fiduciary,
        selectedClient: this.selectedClient,
        fundID: this.data.fundID,
      }).then((result) => {
        if (result.status.success) {
          const { data = [], rowData = [] } = result;
          this.rowData = rowData;
          this._formatData(data);
        }
        this.loading = false;
      }).catch((error) => {
        this.loading = false;
        const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
        if (httpStatus === 401) {
          !this.isLogoutTheCurrentRoute
              && this.$router.push({ name: 'logout' });
        } else {
          this.notifyError({
            error,
            section: 'lifecycle:created',
            data: { url: this.requestUrl },
            notificaction: {
              title: 'Fetching ShareClasses for Status Changes Failed',
              message: `Error: ${error.message}`,
            },
          });
        }
      });
    } else {
      this._formatData(this.data.subs);
      this.loading = false;
    }
  },
  methods: {
    ...mapActions(['logError']),
    getShareclassDisplayName(shareclass) {
      const displayName = get(shareclass, 'displayName', get(shareclass, 'fundName', ''));
      return displayName;
    },
    _orderData(rawData) {
      if (!this.areFundSeries) {
        const dataWithoutSelectedFundInfo = rawData
          .filter((item) => (item.secID || item.secId) !== this.selectedFund.secID);
        const orderedData = orderBy(
          dataWithoutSelectedFundInfo,
          [(shareclass) => this.getShareclassDisplayName(shareclass).toLowerCase()],
          ['asc'],
        );
        const selectedFundInfo = rawData
          .find((item) => (item.secID || item.secId) === this.selectedFund.secID);
        if (selectedFundInfo) {
          orderedData.unshift(selectedFundInfo);
        }
        return orderedData;
      }
      return orderBy(
        rawData,
        [(shareclass) => this.getShareclassDisplayName(shareclass).toLowerCase()],
        ['asc'],
      );
    },
    _formatData(rawData) {
      try {
        const data = this._orderData(rawData);
        // Shareclasses
        this.shareClasses = data
          // If have pendingStatus
          .map((item) => {
            if (item.pendingStatus) {
              merge(item.status, item.pendingStatus);
            }
            return item;
          })
          // Filter out shareclass with status None
          .filter((item) => (item.status ? item.status.name !== 'None' : true))
          // Transform shareclass data
          .map((item) => {
            /* eslint-disable no-param-reassign */
            item.id = item.fiduciaryFundID;
            // Status text and array of status
            item.statusArray = item.statusArray || [item.status];
            item.status = (item.statusArray.length > 1) ? 'Multiple' : item.statusArray[0].name;
            item.statusId = (item.statusArray.length > 1) ? 0 : item.statusArray[0].id;

            // Set default new status
            item.newStatus = 0;
            item.newStatusText = '';
            item.newStatusIcon = '';

            // Calculate the current status of clients only in All Clients View
            if (this.allClientsView) {
              // Clients
              const clients = [];
              item.fiduciaryClient.forEach((row, index) => {
                // Fallback for no PSA Category
                const clientFund = row.fund;
                const psaCategory = (clientFund.psaCategory && clientFund.psaCategory.length)
                  ? item.psaCategory[index] : {};
                const categoryName = psaCategory.category ? psaCategory.category.name : '';
                const clientFundStatus = clientFund.pendingStatus || clientFund.status;
                clients.push({
                  id: row.client.id,
                  name: row.client.clientName,
                  code: row.client.clientCode,
                  fiduciaryID: row.fiduciary.id,
                  category: categoryName,
                  fund: clientFund,
                  status: clientFundStatus.name,
                  statusId: clientFundStatus.id,
                  newStatus: 0,
                  newStatusText: '',
                  newStatusIcon: '',
                  isPending: clientFund.isPending,
                });
              });
              item.clients = orderBy(clients, [(client) => client.name.toLowerCase()], ['asc']);
              item.displayClients = false;
            }

            // Options for change status
            const statuses = item.statusArray.map((status) => status.name);
            const index = getSchemaIndexFromStatus(this.schema, statuses);
            const options = applySchemaToOptions(this.schema[index].options, this.defaultOptions);
            item.options = options;

            /* eslint-enable no-param-reassign */
            return item;
          });
      } catch (error) {
        this.logError({
          errorFrom: 'ChangeStatus:methods:_formatData',
          details: {
            ...Object.getOwnPropertyDescriptors(error),
            message: error.message,
          },
        });
      }
      return this.shareClasses;
    },
    _resetFundNewStatus(shareclasses) {
      // Check if any shareclasses have a different status to update fund new status
      const [first = { newStatus: 0 }] = shareclasses;
      const haveSameValue = shareclasses.every((item) => item.newStatus === first.newStatus);
      if (!haveSameValue) {
        // Select option
        this.applyGlobalStatus = false;
      } else {
        // Same status from shareclasses
        this.newStatus = first.newStatus;
      }
    },
    _haveSomeChangeStatus(shareclasses) {
      // Check if any shareclasses have a different status from the default
      this.disableSubmit = !shareclasses.some((item) => item.newStatus !== 0);
    },
    _status(statuses, key) {
      if (statuses.length > 1) {
        return 'Multiple';
      }
      return extractNameByMayorCount(statuses, key);
    },
    _getFiduciaryClient(fund, index = 0) {
      if (Array.isArray(fund.fiduciaryClient) && fund.fiduciaryClient[index]) {
        return fund.fiduciaryClient[index];
      }
      return fund.fiduciaryClient || {};
    },
    _formatAllClientsPayload(items) {
      const payload = [];
      items.forEach((item) => {
        item.clients.forEach((client, index) => {
          const fiduciaryClient = this._getFiduciaryClient(client.fund, index);
          const object = {
            fundName: this.getShareclassDisplayName(item),
            ticker: client.fund.ticker || '',
            secID: client.fund.secId,
            fiduciaryFundID: client.fund.fiduciaryFundID,
            fiduciaryID: fiduciaryClient.fiduciary.id,
            fiduciaryClient,
          };
          object.fiduciaryID = client.fiduciaryID;
          object.fiduciaryClient = fiduciaryClient;
          object.previousStatus = {
            id: client.statusId,
            name: client.status,
          };
          object.updatedStatus = {
            id: client.newStatus,
            name: client.newStatusIcon,
          };
          // Add change if the client:
          // 1. Have a new status
          // 2. The new status is different from the current status
          // TODO: 3. The fiduciaryFundID is uniq into the entire payload
          //          (It should be Temp) We need to fix this in the all clients view
          if (client.newStatus !== 0
            && client.statusId !== client.newStatus
            && !payload.some((payloadItem) => payloadItem.fiduciaryFundID === object.fiduciaryFundID)
          ) {
            payload.push(object);
          }
        });
      });
      return payload;
    },
    _formatOneClientPayload(items) {
      const payload = [];
      items.forEach((item) => {
        const fiduciaryClient = this._getFiduciaryClient(item);
        const object = {
          fundName: item.fundName,
          ticker: item.ticker || '',
          secID: item.secID || item.secId,
          fiduciaryFundID: item.id || item.fiduciaryFundID,
          fiduciaryID: fiduciaryClient.fiduciary.id,
          fiduciaryClient,
        };
        object.previousStatus = {
          id: item.statusId,
          name: item.status,
        };
        object.updatedStatus = {
          id: item.newStatus,
          name: item.newStatusIcon,
        };
        // Add change if the client:
        // 1. Have a new status
        // 2. The new status is different from the current status
        if (item.newStatus !== 0 && item.statusId !== item.newStatus) {
          payload.push(object);
        }
      });
      return payload;
    },
    selectNewStatusFund(newStatus) {
      this.newStatus = newStatus;
    },
    selectNewStatusShareclass(...args) {
      const [newStatus, index] = args;
      this.shareClasses[index].newStatus = newStatus;
    },
    toggleClient(...args) {
      const [display, index] = args;
      this.shareClasses[index].displayClients = display;
    },
    saveChanges() {
      this.processing = true;

      let payload = [];
      if (this.allClientsView) {
        // All Clients View
        payload = this._formatAllClientsPayload(this.shareClasses);
      } else {
        // 1 Client View
        payload = this._formatOneClientPayload(this.shareClasses);
      }

      // If any changes for submit
      if (payload.length) {
        PendingFundsService.saveToPendingFundStatusChanges({
          payload,
          areTargetSeriesFunds: this.data.__isTargetSeries,
          allClientsView: this.allClientsView,
        }).then((result) => {
          reviewErrorFromResponse(result);
          if (result.status.success) {
            this.$store.dispatch('performActionFullscreenModal', true);
            this.$store.dispatch('closeFullscreenModal');
            this.$store.dispatch('openNotification', {
              message: 'Status Change Added to Pending',
            });
          }
          return result;
        }).catch((error) => {
          const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
          if (httpStatus === 401) {
            !this.isLogoutTheCurrentRoute
              && this.$router.push({ name: 'logout' });
          } else {
            this.notifyError({
              error,
              section: 'methods:saveChanges',
              data: { url: this.requestUrl },
              notificaction: {
                title: 'Save Status Changes Failed',
                message: `Error: ${error.message}`,
              },
            });
          }
        }).finally(() => {
          this.processing = false;
        });
      } else {
        this.processing = false;
      }
    },
  },
  watch: {
    newStatus(newValue, oldValue) {
      const option = find(this.defaultOptions, { value: newValue });
      this.newStatusText = option.label;
      this.newStatusIcon = option.icon;

      // If the new state for global fund was not force by a shareclass,
      // apply the global status to all shareclasses
      if (newValue !== oldValue) {
        this.applyGlobalStatus = true;
      }

      if (this.applyGlobalStatus) {
        this.shareClasses.map((item) => {
          /* eslint-disable no-param-reassign */
          // If the option is available to select at shareclass level
          if (!find(item.options, { value: newValue }).config.disabled
            || newValue === this.emptyOption.value) {
            item.newStatus = newValue;
            item.newStatusText = this.newStatusText;
            item.newStatusIcon = this.newStatusIcon;
          }
          /* eslint-enable no-param-reassign */
          return item;
        });
      }
      this._resetFundNewStatus(this.shareClasses);
    },
    shareClasses: {
      handler(newValue) {
        /* eslint-disable no-param-reassign */
        newValue.map((item) => {
          const option = find(this.defaultOptions, { value: item.newStatus });
          item.newStatusText = option.label;
          item.newStatusIcon = option.icon;
          // Calculate the new status of clients only in All Clients View
          if (this.allClientsView) {
            item.clients.map((client) => {
              // In All Clients View the removed status for client can't be changed
              if (client.status !== 'Removed' || item.newStatus === 0) {
                client.newStatus = item.newStatus;
                client.newStatusText = item.newStatusText;
                client.newStatusIcon = item.newStatusIcon;
              } else {
                client.newStatus = 2;
                client.newStatusText = 'Removed';
                client.newStatusIcon = 'Removed';
              }
              return client;
            });
          }
          return item;
        });
        this._resetFundNewStatus(newValue);
        this._haveSomeChangeStatus(newValue);
        /* eslint-enable no-param-reassign */
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.requestController && this.requestController.abort();
  },
};
</script>
<style lang="scss">
.change-status {
  &__container {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
  }
  &__content {
    $box-height: calc(100% - 152px - 32px); // Substract: header height + actions height
    display: flex;
    flex-direction: column;
    flex: auto;
    min-height: $box-height;
    max-height: $box-height;
    padding: $mds-space-4-x $mds-space-8-x;
  }
  &__text-bold {
    font-weight: $mds-font-weight-bold;
  }
  &__text-sub {
    color: $mds-text-color-secondary-default;
  }
  &__actions {
    flex: initial;
    padding: 0 $mds-space-inset-8-x $mds-space-inset-4-x $mds-space-inset-8-x;
    text-align: right;
  }
}
.shareclasses-data {
  &__fund {
    margin-bottom: $mds-space-4-x;
  }
  &__container {
    flex: auto;
    margin-top: $mds-space-inset-2-x;
    overflow-x: hidden;
    overflow-y: auto;
  }
}
</style>
